import { DebounceInterval } from 'model';
import { useEffect, useRef, useState } from 'react';
import { TimeoutId } from '@reduxjs/toolkit/dist/query/core/buildMiddleware/types';

/**
 * This is a component life cycle debounce that can span renderers and allows
 * updating the callback function 'in flight'.
 *
 * You can call this repeatedly in a render loop and change the function
 * That will be called based on the results of other hooks. This differs from
 * a standard debounce function, where if you generate a new debounce function you
 * break the debouncing, and if you keep the same debounce function you are stuck
 * with stale closures.
 *
 * @param {Function} func A function to be debounced
 * @param {DebounceInterval} debounceInterval The debounce interval
 * @returns {wrapper, result} The wrapper function and the result of the last call
 */

export function useDebounce<E, T>(
	func: (args: E) => T,
	debounceInterval: DebounceInterval
): { wrapper: (args: E) => void; result: T | null } {
	const timer = useRef<TimeoutId>();
	const callBackfunc = useRef<(args: E) => T>();
	const [result, setResult] = useState<T | null>(null);
	callBackfunc.current = func;
	const wrapper = (args: E) => {
		if (timer.current) {
			clearTimeout(timer.current);
		}
		timer.current = setTimeout(() => {
			timer.current = undefined;
			if (callBackfunc.current) {
				setResult(callBackfunc.current(args));
			}
		}, debounceInterval);
	};
	useEffect(() => {
		return () => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
	}, []);
	return { wrapper, result };
}
